/**
 * ActivityTypeIcon (Sprint 4.3.3, PROD-652).
 *
 * Inline-SVG icons for the 6 native activity types per the Awell
 * orchestration domain model:
 *   - api_call    cloud
 *   - calculation formula sigma
 *   - checklist   checkbox-list
 *   - form        paper / document
 *   - message     speech-bubble
 *   - reminder    bell
 *
 * Reference: `03-knowledge-base/external-references/awell/orchestration/
 * api-reference/overview/domain-model.md` (the domain-model section
 * `Activities use a generic structure (subject + action + object +
 * indirect object)`) and the Scrips canon at
 * `03-knowledge-base/domains/clinical/pathway-engineering/
 * six-native-activity-types.md`.
 *
 * Sprint 1.2 deferred the design-first Figma source for these icons; v0
 * ships hand-rolled inline SVGs with no inline hex (every fill / stroke
 * resolves through Tailwind `currentColor` so consumers control color
 * via the surrounding `text-*` token).
 *
 * HC2 token-only: stroke + fill use `currentColor`. Sizing is keyed off
 * a closed `'sm' | 'md' | 'lg'` enum (16 / 20 / 28 px) so calls cannot
 * smuggle in arbitrary px values.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: every icon carries `role="img"` and an `aria-label` keyed
 * off the activity type label registry.
 */

export const ACTIVITY_ICON_TYPES = [
  'api_call',
  'calculation',
  'checklist',
  'form',
  'message',
  'reminder',
] as const;

export type ActivityIconType = (typeof ACTIVITY_ICON_TYPES)[number];

export type ActivityIconSize = 'sm' | 'md' | 'lg';

/**
 * Closed labels surfaced as the icon `aria-label`. The label is the
 * single source of truth for a11y announcements; consumers MUST NOT
 * override it inline so the SR experience stays consistent.
 */
export const ACTIVITY_TYPE_LABELS: Record<ActivityIconType, string> = {
  api_call: 'API Call',
  calculation: 'Calculation',
  checklist: 'Checklist',
  form: 'Form',
  message: 'Message',
  reminder: 'Reminder',
};

const SIZE_PX: Record<ActivityIconSize, number> = {
  sm: 16,
  md: 20,
  lg: 28,
};

export interface ActivityTypeIconProps {
  type: ActivityIconType;
  size?: ActivityIconSize;
  /** Optional class merged onto the root `<svg>` for layout / spacing. */
  className?: string;
}

export function ActivityTypeIcon({
  type,
  size = 'md',
  className,
}: ActivityTypeIconProps) {
  const px = SIZE_PX[size];
  const label = ACTIVITY_TYPE_LABELS[type];
  return (
    <svg
      role="img"
      aria-label={label}
      data-testid={`activity-type-icon-${type}`}
      data-activity-type={type}
      data-size={size}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {renderIconPath(type)}
    </svg>
  );
}

function renderIconPath(type: ActivityIconType) {
  switch (type) {
    case 'api_call':
      // Cloud silhouette evoking an HTTP / API integration.
      return (
        <path d="M7 18a4 4 0 0 1-.88-7.9 5 5 0 0 1 9.78-1.32A4.5 4.5 0 0 1 17 18Z" />
      );
    case 'calculation':
      // Sigma summation glyph for "compute a score".
      return (
        <>
          <path d="M17 5H7l5 7-5 7h10" />
        </>
      );
    case 'checklist':
      // Checkbox list with two ticked rows.
      return (
        <>
          <rect x="3" y="4" width="6" height="6" rx="1" />
          <path d="m4.5 7 1.5 1.5L8 6.5" />
          <rect x="3" y="14" width="6" height="6" rx="1" />
          <path d="m4.5 17 1.5 1.5L8 16.5" />
          <path d="M12 7h9" />
          <path d="M12 17h9" />
        </>
      );
    case 'form':
      // Document with three text rows.
      return (
        <>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
          <path d="M14 3v6h6" />
          <path d="M8 13h8" />
          <path d="M8 17h6" />
        </>
      );
    case 'message':
      // Speech bubble.
      return (
        <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2Z" />
      );
    case 'reminder':
      // Bell with clapper.
      return (
        <>
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </>
      );
  }
}

export { SIZE_PX as ACTIVITY_ICON_SIZE_PX };
